/**
 * ParticleEmitter — manages particle state, emission, and simulation.
 *
 * Stores particle data in Float32Arrays (SoA) suitable for GPU upload.
 * Handles emission scheduling, lifetime management, and CPU-side simulation.
 */

import * as THREE from 'three/webgpu';
import {
  type ParticleConfig,
  type BurstConfig,
  type CurveKeyframe,
  type GradientKeyframe,
  type EmitterShape,
  PARTICLE_STRIDE,
  DEFAULT_PARTICLE_CONFIG,
} from './types.js';

const _tempVec = new THREE.Vector3();
const _tempColor = new THREE.Color();

export class ParticleEmitter {
  readonly config: ParticleConfig;
  readonly maxParticles: number;

  /** SoA particle data: position(3), velocity(3), age, lifetime, size, r,g,b,a, rotation, startSize, startSpeed */
  readonly data: Float32Array;

  /** Number of currently alive particles */
  activeCount = 0;

  /** Emission accumulator */
  private emitAccumulator = 0;
  /** Continuous emission rate set via setEmissionRate(); null = use config. */
  private _rateOverride: number | null = null;
  private burstTimers: { config: BurstConfig; nextTime: number; cyclesLeft: number }[] = [];
  private elapsed = 0;
  private _playing = true;

  /** World-space transform for world-space simulation */
  readonly worldMatrix = new THREE.Matrix4();

  constructor(config?: Partial<ParticleConfig>) {
    this.config = { ...DEFAULT_PARTICLE_CONFIG, ...config };
    this.maxParticles = this.config.maxParticles;
    this.data = new Float32Array(this.maxParticles * PARTICLE_STRIDE);

    // Initialize burst timers
    if (this.config.emission?.bursts) {
      for (const burst of this.config.emission.bursts) {
        this.burstTimers.push({
          config: burst,
          nextTime: burst.time,
          cyclesLeft: burst.cycles === 0 ? -1 : (burst.cycles ?? 1),
        });
      }
    }
  }

  get playing(): boolean { return this._playing; }

  play(): void { this._playing = true; }
  pause(): void { this._playing = false; }
  stop(): void {
    this._playing = false;
    this.activeCount = 0;
    this.elapsed = 0;
    this.emitAccumulator = 0;
    // Reset burst timers too, or a stop()->play() restart silently loses bursts
    // (their nextTime stayed advanced and finite cyclesLeft stayed at 0).
    for (const timer of this.burstTimers) {
      timer.nextTime = timer.config.time;
      timer.cyclesLeft = timer.config.cycles === 0 ? -1 : (timer.config.cycles ?? 1);
    }
  }

  /**
   * Update particle simulation for one frame.
   */
  update(dt: number): void {
    if (!this._playing) return;
    this.elapsed += dt;

    // 1. Age and kill dead particles
    this._ageParticles(dt);

    // 2. Emit new particles
    this._emitContinuous(dt);
    this._emitBursts();

    // 3. Simulate forces
    this._simulate(dt);

    // 4. Apply over-lifetime modules
    this._applyOverLifetime(dt);
  }

  /**
   * Set the continuous emission rate (particles/sec). Stored as an override so
   * the rate can change after create without mutating the read-only `config`.
   */
  setEmissionRate(rate: number): void {
    this._rateOverride = rate;
  }

  /**
   * Emit N particles immediately.
   */
  emit(count: number): void {
    for (let i = 0; i < count && this.activeCount < this.maxParticles; i++) {
      this._spawnParticle();
    }
  }

  // ─── Private ────────────────────────────────────────────────────────────

  private _ageParticles(dt: number): void {
    const data = this.data;
    let writeIdx = 0;

    for (let i = 0; i < this.activeCount; i++) {
      const offset = i * PARTICLE_STRIDE;
      data[offset + 6] += dt; // age += dt

      if (data[offset + 6] < data[offset + 7]) {
        // Particle still alive — compact if needed
        if (writeIdx !== i) {
          const dst = writeIdx * PARTICLE_STRIDE;
          for (let j = 0; j < PARTICLE_STRIDE; j++) {
            data[dst + j] = data[offset + j];
          }
        }
        writeIdx++;
      }
    }

    this.activeCount = writeIdx;
  }

  private _emitContinuous(dt: number): void {
    // setEmissionRate() override wins over config so the rate can be changed
    // after create without mutating the read-only config.
    const rate = this._rateOverride ?? this.config.emission?.rateOverTime ?? 0;
    if (rate <= 0) return;

    this.emitAccumulator += rate * dt;
    const toEmit = Math.floor(this.emitAccumulator);
    this.emitAccumulator -= toEmit;

    this.emit(toEmit);
  }

  private _emitBursts(): void {
    for (const timer of this.burstTimers) {
      if (this.elapsed >= timer.nextTime && timer.cyclesLeft !== 0) {
        this.emit(timer.config.count);
        if (timer.cyclesLeft > 0) timer.cyclesLeft--;
        // cyclesLeft < 0 means infinite — leave unchanged
        timer.nextTime += timer.config.interval || (1 / 60);
      }
    }
  }

  private _spawnParticle(): void {
    if (this.activeCount >= this.maxParticles) return;
    const offset = this.activeCount * PARTICLE_STRIDE;
    const data = this.data;

    // Position from shape
    const pos = this._sampleShape();
    data[offset + 0] = pos.x;
    data[offset + 1] = pos.y;
    data[offset + 2] = pos.z;

    // Velocity from shape direction + startSpeed
    const speed = this._randomRange(this.config.startSpeed);
    const dir = this._sampleDirection();
    data[offset + 3] = dir.x * speed;
    data[offset + 4] = dir.y * speed;
    data[offset + 5] = dir.z * speed;

    // Age = 0, lifetime
    data[offset + 6] = 0;
    data[offset + 7] = this._randomRange(this.config.lifetime);

    // Size
    const size = this._randomRange(this.config.startSize);
    data[offset + 8] = size;

    // Color
    _tempColor.set(this.config.startColor);
    data[offset + 9] = _tempColor.r;
    data[offset + 10] = _tempColor.g;
    data[offset + 11] = _tempColor.b;
    data[offset + 12] = 1.0; // alpha

    // Rotation
    data[offset + 13] = this._randomRange(this.config.startRotation ?? 0);

    // Store initial values for over-lifetime curves
    data[offset + 14] = size;   // startSize
    data[offset + 15] = speed;  // startSpeed

    // Transform to world space if needed
    if (this.config.simulationSpace === 'world') {
      _tempVec.set(data[offset], data[offset + 1], data[offset + 2]);
      _tempVec.applyMatrix4(this.worldMatrix);
      data[offset] = _tempVec.x;
      data[offset + 1] = _tempVec.y;
      data[offset + 2] = _tempVec.z;

      // Transform velocity direction into world space, preserving magnitude.
      // transformDirection() normalizes, which would discard startSpeed.
      const velDir = _tempVec.set(data[offset + 3], data[offset + 4], data[offset + 5]);
      const velLen = velDir.length();
      velDir.transformDirection(this.worldMatrix);
      data[offset + 3] = velDir.x * velLen;
      data[offset + 4] = velDir.y * velLen;
      data[offset + 5] = velDir.z * velLen;
    }

    this.activeCount++;
  }

  private _simulate(dt: number): void {
    const gravity = (this.config.gravityModifier ?? 0) * -9.81;
    const data = this.data;
    // Loop-invariant noise params + scroll time hoisted out of the per-particle
    // loop (they were destructured and `t` recomputed for every particle).
    const noise = this.config.noise;
    const strength = noise ? noise.strength : 0;
    const frequency = noise ? noise.frequency : 0;
    const t = noise ? this.elapsed * noise.scrollSpeed : 0;

    for (let i = 0; i < this.activeCount; i++) {
      const o = i * PARTICLE_STRIDE;

      // Apply gravity to velocity
      data[o + 4] += gravity * dt;

      // Apply noise force
      if (noise) {
        const px = data[o] * frequency + t;
        const py = data[o + 1] * frequency + t;
        const pz = data[o + 2] * frequency + t;
        // Simple pseudo-noise (sin-based, cheap)
        data[o + 3] += Math.sin(px * 1.7 + py * 2.3) * strength * dt;
        data[o + 4] += Math.sin(py * 2.1 + pz * 1.9) * strength * dt;
        data[o + 5] += Math.sin(pz * 1.3 + px * 2.7) * strength * dt;
      }

      // Integrate position
      data[o + 0] += data[o + 3] * dt;
      data[o + 1] += data[o + 4] * dt;
      data[o + 2] += data[o + 5] * dt;
    }
  }

  private _applyOverLifetime(dt?: number): void {
    const data = this.data;
    const sizeCurve = this.config.sizeOverLifetime;
    const colorGradient = this.config.colorOverLifetime;
    const speedCurve = this.config.speedOverLifetime;
    const alphaCurve = this.config.alphaOverLifetime;
    const rotSpeed = this.config.rotationOverLifetime ?? 0;

    if (!sizeCurve && !colorGradient && !speedCurve && !alphaCurve && rotSpeed === 0) return;

    for (let i = 0; i < this.activeCount; i++) {
      const o = i * PARTICLE_STRIDE;
      // Guard zero-lifetime particles (lifetime=0 from user config). Without
      // this, the division yields NaN which poisons the particle's Float32
      // slots and propagates through sampleCurve/sampleGradient.
      const life = data[o + 7];
      const t = life > 0 ? data[o + 6] / life : 1; // normalizedAge = age/lifetime

      if (sizeCurve) {
        data[o + 8] = data[o + 14] * _sampleCurve(sizeCurve, t);
      }

      if (colorGradient) {
        const c = _sampleGradient(colorGradient, t);
        data[o + 9] = c.r;
        data[o + 10] = c.g;
        data[o + 11] = c.b;
      }

      if (alphaCurve) {
        data[o + 12] = _sampleCurve(alphaCurve, t);
      }

      if (speedCurve) {
        const speedMul = _sampleCurve(speedCurve, t);
        const len = Math.sqrt(data[o+3]**2 + data[o+4]**2 + data[o+5]**2);
        if (len > 0.0001) {
          const target = data[o + 15] * speedMul;
          const scale = target / len;
          data[o + 3] *= scale;
          data[o + 4] *= scale;
          data[o + 5] *= scale;
        }
      }

      if (rotSpeed !== 0 && dt) {
        data[o + 13] += rotSpeed * dt;
      }
    }
  }

  private _sampleShape(): THREE.Vector3 {
    const shape: EmitterShape = this.config.shape ?? { type: 'point' };

    switch (shape.type) {
      case 'point':
        return _tempVec.set(0, 0, 0);

      case 'sphere': {
        const u = Math.random();
        const v = Math.random();
        const theta = 2 * Math.PI * u;
        const phi = Math.acos(2 * v - 1);
        const r = shape.radius * Math.cbrt(Math.random());
        return _tempVec.set(
          r * Math.sin(phi) * Math.cos(theta),
          r * Math.sin(phi) * Math.sin(theta),
          r * Math.cos(phi),
        );
      }

      case 'hemisphere': {
        const u = Math.random();
        const v = Math.random();
        const theta = 2 * Math.PI * u;
        const phi = Math.acos(v); // Only upper hemisphere
        const r = shape.radius * Math.cbrt(Math.random());
        return _tempVec.set(
          r * Math.sin(phi) * Math.cos(theta),
          r * Math.cos(phi),
          r * Math.sin(phi) * Math.sin(theta),
        );
      }

      case 'box':
        return _tempVec.set(
          (Math.random() - 0.5) * 2 * shape.halfExtents.x,
          (Math.random() - 0.5) * 2 * shape.halfExtents.y,
          (Math.random() - 0.5) * 2 * shape.halfExtents.z,
        );

      case 'cone': {
        const t = Math.random();
        const r = shape.radius * Math.sqrt(Math.random());
        const theta = 2 * Math.PI * Math.random();
        return _tempVec.set(
          r * Math.cos(theta),
          t * shape.height,
          r * Math.sin(theta),
        );
      }

      case 'circle': {
        const r = shape.radius * Math.sqrt(Math.random());
        const theta = 2 * Math.PI * Math.random();
        return _tempVec.set(r * Math.cos(theta), 0, r * Math.sin(theta));
      }

      default:
        return _tempVec.set(0, 0, 0);
    }
  }

  private _sampleDirection(): THREE.Vector3 {
    const shape: EmitterShape = this.config.shape ?? { type: 'point' };

    switch (shape.type) {
      case 'cone': {
        // Uniform solid-angle sampling in the cone around +Y. The
        // previous `angle * sqrt(random)` biased particles toward the
        // cone rim; the canonical formula samples cosφ uniformly in
        // [cos(angle), 1] so particles are evenly distributed over the
        // cap surface.
        const cosMax = Math.cos(shape.angle);
        const cosPhi = cosMax + (1 - cosMax) * Math.random();
        const sinPhi = Math.sqrt(Math.max(0, 1 - cosPhi * cosPhi));
        const theta = 2 * Math.PI * Math.random();
        return _tempVec.set(
          sinPhi * Math.cos(theta),
          cosPhi,
          sinPhi * Math.sin(theta),
        ).normalize();
      }

      case 'sphere': {
        const u = Math.random();
        const v = Math.random();
        const theta = 2 * Math.PI * u;
        const phi = Math.acos(2 * v - 1);
        return _tempVec.set(
          Math.sin(phi) * Math.cos(theta),
          Math.sin(phi) * Math.sin(theta),
          Math.cos(phi),
        ).normalize();
      }

      case 'hemisphere': {
        const u = Math.random();
        const v = Math.random();
        const theta = 2 * Math.PI * u;
        const phi = Math.acos(v); // φ ∈ [0, π/2] — upper hemisphere
        return _tempVec.set(
          Math.sin(phi) * Math.cos(theta),
          Math.cos(phi),
          Math.sin(phi) * Math.sin(theta),
        ).normalize();
      }

      default:
        return _tempVec.set(0, 1, 0);
    }
  }

  private _randomRange(value: number | { min: number; max: number }): number {
    if (typeof value === 'number') return value;
    return value.min + Math.random() * (value.max - value.min);
  }
}

// ─── Curve / Gradient Helpers ─────────────────────────────────────────────

function _sampleCurve(keys: CurveKeyframe[], t: number): number {
  if (keys.length === 0) return 1;
  if (t <= keys[0].time) return keys[0].value;
  if (t >= keys[keys.length - 1].time) return keys[keys.length - 1].value;

  for (let i = 0; i < keys.length - 1; i++) {
    if (t >= keys[i].time && t <= keys[i + 1].time) {
      const f = (t - keys[i].time) / (keys[i + 1].time - keys[i].time);
      return keys[i].value + f * (keys[i + 1].value - keys[i].value);
    }
  }
  return keys[keys.length - 1].value;
}

const _gradColor = new THREE.Color();

function _sampleGradient(keys: GradientKeyframe[], t: number): THREE.Color {
  if (keys.length === 0) return _gradColor.setRGB(1, 1, 1);
  // Always return through `_gradColor` scratch so callers receive a stable
  // reference contract: every code path returns the same instance, never a
  // live reference into the caller-owned keyframe array. (Returning
  // `keys[i].color` directly let downstream consumers hold an aliased
  // reference that the next call would silently mutate.)
  if (t <= keys[0].time) return _gradColor.copy(keys[0].color);
  if (t >= keys[keys.length - 1].time) return _gradColor.copy(keys[keys.length - 1].color);

  for (let i = 0; i < keys.length - 1; i++) {
    if (t >= keys[i].time && t <= keys[i + 1].time) {
      const f = (t - keys[i].time) / (keys[i + 1].time - keys[i].time);
      _gradColor.copy(keys[i].color).lerp(keys[i + 1].color, f);
      return _gradColor;
    }
  }
  return _gradColor.copy(keys[keys.length - 1].color);
}
